<template>
  <BaseModal :modalTitle="$t('W: Rediger prøve #{number}', { number: sampleNumber })">
    <div class="flex flex-col gap-5">
      <p
        v-if="!order || !sample"
        class="text-sm text-text-muted">
        {{ $t('W: Prøven blev ikke fundet') }}
      </p>

      <template v-else>
        <!-- Sample area -->
        <UiComboboxVee
          name="sampleAreaID"
          :label="$t('W: Prøveområde')"
          :options="sampleAreaOptions"
          :modelValue="selectedSampleAreaID"
          @update:model-value="onAreaChange" />

        <!-- Analyses -->
        <div
          v-if="selectedSampleAreaID"
          class="flex flex-col gap-2">
          <div
            v-if="availableAnalyses.length === 0"
            class="text-sm text-text-muted">
            {{ $t('W: Ingen analyser tilgængelige for dette område') }}
          </div>
          <template v-else>
            <UiComboboxMultiVee
              name="analysisIDs"
              :label="$t('W: Analyser')"
              :options="analysisOptions"
              v-model="selectedAnalysisIDs" />
            <p
              v-if="hasOnlyOnePerSample"
              class="text-sm text-text-muted">
              {{ $t('W: Denne prøve kan kun have én analyse') }}
            </p>
            <p
              v-if="selectedAnalysisIDs.length === 0"
              class="text-sm text-feedback-warning">
              {{ $t('W: Vælg mindst én analyse') }}
            </p>
          </template>
        </div>

        <!-- Delivery -->
        <div
          v-if="selectedAnalysisIDs.length > 0"
          class="flex flex-col gap-2">
          <span class="text-sm font-semibold text-text-primary">
            {{ $t('W: Levering') }}
          </span>

          <UiInputRadioGroupVee
            name="delivery"
            :options="deliveryOptions"
            v-model="selectedDeliveryKey" />

          <div
            v-if="selectedDeliveryKey === 'new'"
            class="pl-7">
            <UiInputSelectVee
              name="newDeliveryTatID"
              :label="$t('W: Svartid')"
              :options="newDeliveryTatOptions"
              :modelValue="newDeliveryTatID"
              @update:model-value="newDeliveryTatID = $event" />
          </div>

          <p
            v-if="!selectedDeliveryKey"
            class="text-sm text-feedback-warning">
            {{ $t('W: Vælg en levering') }}
          </p>
        </div>

        <p
          v-if="errorText"
          class="text-sm text-feedback-error">
          {{ errorText }}
        </p>
      </template>
    </div>

    <template #buttons>
      <UiBaseButton
        :text="$t('W: Gem')"
        :isLoading="isLoading"
        :showSaveIcon="showSaveIcon"
        :isDisabled="!canSubmit || isLoading"
        type="button"
        @button-click="onSubmit" />
    </template>
  </BaseModal>
</template>

<script lang="ts">
import { mapActions, mapState } from 'pinia'
import { useOrderStore } from '@/stores/orderStore'
import { useAppStore } from '@/stores/appStore'
import { useTurnaroundTimeStore } from '@/stores/turnaroundTimeStore'
import { useAnalysisStore } from '@/stores/analysisStore'
import { useSampleAreaStore } from '@/stores/sampleAreaStore'
import { getRefusalReason } from '@/globals/typescript/utils/apiRefusal'
import { useToast } from '@/globals/typescript/utils/toast'
import { useModalStore } from '@/stores/modalStore'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'
import { getOrderedDeliveries } from '@team-milva/lab-utils/orderDeliveries'
import type { PropType } from 'vue'

type ISelectOption = { value: string; text: string }

export default {
  name: 'EditSampleModal',

  emits: ['close'],

  props: {
    labAccountID: { type: String as PropType<string>, required: true },
    orderID: { type: String as PropType<string>, required: true },
    sampleID: { type: String as PropType<string>, required: true },
    sampleNumber: { type: Number as PropType<number>, required: true },
  },

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      selectedSampleAreaID: '',
      selectedAnalysisIDs: [] as string[],
      selectedDeliveryKey: '',
      newDeliveryTatID: '',
      isLoading: false,
      showSaveIcon: false,
      errorText: '',
    }
  },

  computed: {
    ...mapState(useOrderStore, ['allOrders']),
    ...mapState(useAppStore, ['currentLocale', 'defaultMessageSaveIconTime']),
    ...mapState(useTurnaroundTimeStore, ['allTATs']),
    ...mapState(useAnalysisStore, ['allAnalyses']),
    ...mapState(useSampleAreaStore, ['allSampleAreas']),

    order() {
      return this.allOrders.find((x) => x.id === this.orderID)
    },

    sample() {
      return this.order?.samples.find((x) => x.sampleID === this.sampleID)
    },

    sampleAreaOptions(): ISelectOption[] {
      return this.allSampleAreas
        .filter((x) => x.accountID === this.labAccountID && x.activeState === 'active')
        .sort((a, b) =>
          a.getName(this.currentLocale).localeCompare(b.getName(this.currentLocale))
        )
        .map((area) => ({ value: area.id, text: area.getName(this.currentLocale) }))
    },

    availableAnalyses() {
      if (!this.order || !this.selectedSampleAreaID) return []
      return this.allAnalyses
        .filter((analysis) => {
          if (analysis.accountID !== this.labAccountID) return false
          if (analysis.activeState !== 'active') return false
          if (!analysis.sampleAreaIDs.includes(this.selectedSampleAreaID)) return false
          return true
        })
        .sort((a, b) =>
          a.getName(this.currentLocale).localeCompare(b.getName(this.currentLocale))
        )
    },

    selectedAnalysisObjects() {
      return this.selectedAnalysisIDs
        .map((id) => this.allAnalyses.find((x) => x.id === id))
        .filter((x) => x !== undefined)
    },

    analysisOptions(): ISelectOption[] {
      if (this.hasOnlyOnePerSample) {
        return this.selectedAnalysisObjects.map((analysis) => ({
          value: analysis.id,
          text: analysis.getName(this.currentLocale),
        }))
      }
      const hasRegularAnalysisSelected = this.selectedAnalysisIDs.length > 0
      return this.availableAnalyses
        .filter((x) => {
          if (hasRegularAnalysisSelected && x.options.isOnlyOnePerSample) return false
          return true
        })
        .map((analysis) => ({
          value: analysis.id,
          text: analysis.getName(this.currentLocale),
        }))
    },

    hasOnlyOnePerSample(): boolean {
      return this.selectedAnalysisObjects.some((x) => x.options.isOnlyOnePerSample)
    },

    availableDeliveries() {
      if (!this.order) return []
      return getOrderedDeliveries(this.order.tatGroups, this.allTATs, this.currentLocale, this.order.samples).filter(
        (delivery) => {
          if (delivery.isLocked) return false
          if (this.selectedAnalysisObjects.length === 0) return true
          const tatInstance = this.allTATs.find((x) => x.id === delivery.tatID)
          if (!tatInstance) return false
          return this.selectedAnalysisObjects.every((analysis) =>
            tatInstance.isCompatibleWithAnalysis(analysis, this.allTATs)
          )
        }
      )
    },

    coveredTatIDs(): Set<string> {
      return new Set(this.availableDeliveries.map((x) => x.tatID))
    },

    deliveryOptions(): { value: string; text: string; description?: string }[] {
      const existing = this.availableDeliveries.map((delivery) => ({
        value: delivery.tatGroupID,
        text: this.$t('W: Levering {number} — {tatName}', {
          number: delivery.deliveryNumber,
          tatName: delivery.tatName,
        }),
        description:
          delivery.tatGroupID === this.sample?.tatGroupID
            ? this.$t('W: (nuværende)')
            : undefined,
      }))
      return [...existing, { value: 'new', text: this.$t('W: Ny levering') }]
    },

    newDeliveryTatOptions(): ISelectOption[] {
      return this.allTATs
        .filter((tat) => {
          if (this.coveredTatIDs.has(tat.id)) return false
          if (this.selectedAnalysisObjects.length === 0) return true
          const instance = this.allTATs.find((x) => x.id === tat.id)
          if (!instance) return false
          return this.selectedAnalysisObjects.every((analysis) =>
            instance.isCompatibleWithAnalysis(analysis, this.allTATs)
          )
        })
        .sort((a, b) =>
          a.getName(this.currentLocale).localeCompare(b.getName(this.currentLocale))
        )
        .map((tat) => ({ value: tat.id, text: tat.getName(this.currentLocale) }))
    },

    canSubmit(): boolean {
      if (!this.order || !this.sample) return false
      if (this.selectedAnalysisIDs.length === 0) return false
      if (!this.selectedDeliveryKey) return false
      if (this.selectedDeliveryKey === 'new' && !this.newDeliveryTatID) return false
      return true
    },
  },

  watch: {
    selectedAnalysisIDs() {
      this.resetDeliveryIfNeeded()
    },

    selectedDeliveryKey(newValue: string) {
      if (newValue !== 'new') this.newDeliveryTatID = ''
    },
  },

  methods: {
    ...mapActions(useOrderStore, ['updateSample']),

    prefillFromSample(): void {
      const sample = this.sample
      if (!sample) return

      const firstActiveAnalysisID = sample.analyses.find(
        (x) => x.timestamps.cancelledAt === null
      )?.analysisID
      const firstAnalysis = firstActiveAnalysisID
        ? this.allAnalyses.find((x) => x.id === firstActiveAnalysisID)
        : null
      this.selectedSampleAreaID = firstAnalysis?.sampleAreaIDs[0] ?? ''

      this.selectedAnalysisIDs = sample.analyses
        .filter((x) => x.timestamps.cancelledAt === null)
        .map((x) => x.analysisID)

      this.selectedDeliveryKey = sample.tatGroupID
    },

    onAreaChange(value: string): void {
      this.selectedSampleAreaID = value
      this.selectedAnalysisIDs = []
      this.selectedDeliveryKey = ''
      this.newDeliveryTatID = ''
    },

    resetDeliveryIfNeeded(): void {
      if (this.selectedDeliveryKey && this.selectedDeliveryKey !== 'new') {
        const stillAvailable = this.availableDeliveries.some(
          (x) => x.tatGroupID === this.selectedDeliveryKey
        )
        if (!stillAvailable) {
          this.selectedDeliveryKey = ''
          this.newDeliveryTatID = ''
        }
      }
      if (this.selectedDeliveryKey === 'new' && this.newDeliveryTatID) {
        const stillAvailable = this.newDeliveryTatOptions.some(
          (x) => x.value === this.newDeliveryTatID
        )
        if (!stillAvailable) this.newDeliveryTatID = ''
      }
    },

    async onSubmit(): Promise<void> {
      if (!this.canSubmit) return
      const sample = this.sample
      if (!sample) return

      const tatID =
        this.selectedDeliveryKey === 'new'
          ? this.newDeliveryTatID
          : this.availableDeliveries.find((x) => x.tatGroupID === this.selectedDeliveryKey)
              ?.tatID ?? ''

      const targetTatGroupID =
        this.selectedDeliveryKey !== 'new' &&
        this.selectedDeliveryKey !== sample.tatGroupID
          ? this.selectedDeliveryKey
          : undefined

      this.isLoading = true
      this.errorText = ''

      try {
        await this.updateSample(
          this.labAccountID,
          this.orderID,
          this.sampleID,
          this.selectedAnalysisIDs,
          this.selectedSampleAreaID,
          tatID,
          targetTatGroupID
        )
        this.showSaveIcon = true
        setTimeout(() => {
          this.showSaveIcon = false
        }, this.defaultMessageSaveIconTime)
        this.unsavedGuard.takeSnapshot()
        useToast().success(this.$t('W: Prøve gemt'))
        this.$emit('close')
      } catch (error) {
        // A refusal the backend words for the user (a locked order, an analysis that cannot share
        // a sample) is shown as is; anything else stays the generic text (MIL-552).
        this.errorText = getRefusalReason(error) ?? this.$t('W: Der opstod en fejl. Prøv igen.')
        useToast().error(this.$t('W: Kunne ikke gemme prøve'))
      } finally {
        this.isLoading = false
      }
    },
  },

  created() {
    this.prefillFromSample()

    // The modal is always the top entry at the moment it is created, so this
    // is its own ModalEntry id — it lets ModalHost tell "the modal I am about
    // to close is dirty" apart from "something underneath it is dirty".
    //
    // Registered after the pre-fill above, so the baseline is the sample's
    // current state and only a real change reads as dirty. The pre-fill moved
    // into its own method for exactly this reason: its early return on a
    // missing sample would otherwise skip the registration too.
    this.unsavedGuard.register({
      snapshot: () => ({
        selectedSampleAreaID: this.selectedSampleAreaID,
        selectedAnalysisIDs: [...this.selectedAnalysisIDs],
        selectedDeliveryKey: this.selectedDeliveryKey,
        newDeliveryTatID: this.newDeliveryTatID,
      }),
      modalId: useModalStore().top?.id,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>
